import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import type { RiskMatrixCellSelectMenuPosition, RiskMatrixCellSelectProps } from '../types'
import {
  RISK_MATRIX_CELL_MENU_Z,
  RISK_MATRIX_CELL_VIEWPORT_GAP,
} from '../constants'
import { getRiskCellTextColor } from '../utils/productRiskMatrixModel'

export function RiskMatrixCellSelect({
  value,
  options,
  onChange,
  disabled = false,
  'aria-label': ariaLabel = 'Risk level',
}: RiskMatrixCellSelectProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<RiskMatrixCellSelectMenuPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const selected = options.find((opt) => opt.value === value)
  const backgroundColor = selected?.color || '#cbd5e1'
  const textColor = getRiskCellTextColor(backgroundColor)

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const menuHeight = menuRef.current?.offsetHeight ?? options.length * 36 + 8
    const spaceBelow = window.innerHeight - rect.bottom - RISK_MATRIX_CELL_VIEWPORT_GAP
    const openUp = spaceBelow < menuHeight && rect.top > spaceBelow
    const top = openUp
      ? Math.max(RISK_MATRIX_CELL_VIEWPORT_GAP, rect.top - menuHeight - 4)
      : rect.bottom + 4
    const minWidth = Math.max(rect.width, 72)
    const left = Math.min(
      Math.max(RISK_MATRIX_CELL_VIEWPORT_GAP, rect.left),
      window.innerWidth - minWidth - RISK_MATRIX_CELL_VIEWPORT_GAP,
    )
    setPosition({ top, left, minWidth, openUp })
  }, [options.length])

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null)
      return
    }
    updatePosition()
  }, [open, updatePosition])

  useEffect(() => {
    if (!open) return
    const onReposition = () => updatePosition()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    window.addEventListener('resize', onReposition)
    window.addEventListener('scroll', onReposition, true)
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      window.removeEventListener('resize', onReposition)
      window.removeEventListener('scroll', onReposition, true)
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open, updatePosition])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        className={cn(
          'flex h-8 w-full items-center justify-center gap-0.5 rounded border border-white/30 px-1 text-xs font-bold outline-none',
          disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer',
          open && 'ring-2 ring-[rgb(21_151_255)]/40',
        )}
        style={{ backgroundColor, color: textColor }}
        onClick={() => {
          if (disabled) return
          setOpen((prev) => !prev)
        }}
      >
        <span>{value}</span>
        <ChevronDown className="h-3 w-3 shrink-0 opacity-80" aria-hidden />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="listbox"
            aria-label={ariaLabel}
            className="fixed max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              minWidth: position?.minWidth ?? 72,
              zIndex: RISK_MATRIX_CELL_MENU_Z,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            {options.map((opt) => {
              const isSelected = opt.value === value
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm font-semibold text-slate-900',
                    isSelected
                      ? 'bg-[rgba(21,151,255,0.12)] text-[rgb(21_151_255)]'
                      : 'hover:bg-slate-50',
                  )}
                  onClick={() => {
                    onChange(opt.value)
                    setOpen(false)
                  }}
                >
                  <span className="inline-flex items-center gap-2">
                    <span
                      className="inline-block h-3 w-3 shrink-0 rounded-sm border border-slate-200"
                      style={{ backgroundColor: opt.color || '#cbd5e1' }}
                      aria-hidden
                    />
                    {opt.value}
                  </span>
                  {isSelected && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                </button>
              )
            })}
          </div>,
          document.body,
        )}
    </>
  )
}
